<template>
  <div class="archimedes-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$F_buoyant = ρ·g·V_dis$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan"><label>排水体积 V_dis</label><span class="val">{{ displacedVol.toFixed(1) }} <small>cm³</small></span></div>
          <div class="hud-item orange"><label>测量弹簧秤 F</label><span class="val">{{ measuredWeight.toFixed(2) }} <small>N</small></span></div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <linearGradient id="water-grad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="#0ea5e9" stop-opacity="0.6"/><stop offset="100%" stop-color="#0369a1" stop-opacity="0.9"/></linearGradient>
             <marker id="arr-up" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#38bdf8" /></marker>
          </defs>

          <!-- 玻璃溢水杯 (Static) -->
          <rect x="100" y="80" width="200" height="150" fill="none" stroke="#334155" stroke-width="2" rx="4" />
          
          <!-- 动态液面 (Water) - 随排水体积自动升降 -->
          <rect x="101" :y="liquidTop" width="198" :height="230 - liquidTop" fill="url(#water-grad)" />

          <!-- 弹簧测力计支架 -->
          <line x1="200" y1="20" x2="200" :y2="blockY - 10" stroke="#94a3b8" stroke-width="1.5" stroke-dasharray="4,2" />
          <rect x="190" :y="blockY - 20" width="20" height="10" fill="#334155" rx="2" />

          <!-- 沉入物体 (Block) - 刚性限制在 [50, 200] -->
          <rect x="170" :y="blockY" width="60" height="60" fill="#64748b" stroke="#1e293b" rx="4" shadow="0 4px 10px rgba(0,0,0,0.5)" />
          <text :x="200" :y="blockY + 35" text-anchor="middle" fill="#fff" font-size="10" font-weight="900">m</text>

          <!-- 受力箭头 (强制对齐中心) -->
          <g v-if="displacedVol > 0" :transform="`translate(200, ${blockY + 30})`">
             <line x1="0" y1="0" x2="0" :y2="-displacedVol/2 - 10" stroke="#38bdf8" stroke-width="3" marker-end="url(#arr-up)" />
             <text x="5" y="-15" fill="#38bdf8" font-size="9" font-weight="900">F_浮</text>
          </g>
        </svg>

        <div class="interaction-hint">
           观察：当物体沉入越深，液面上升越显著，浮力(F_浮)矢量由于排水增加而变长
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">调节物体沉入深度 (Displacement Control)</label>
                 <el-slider v-model="depthVal" :min="0" :max="150" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">液体密度 ρ (0.8~1.2)</label>
                 <el-slider v-model="density" :min="0.8" :max="1.2" :step="0.01" size="small"></el-slider>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-odometer"></i>
           <p><strong>实战物理：</strong>浸在液体里的物体受到向上的浮力。<strong>观察液面：</strong>当物体下挫时，液面同步抬升，这证明了浮力的大小等于<strong>它排开液体的重量</strong>。注意测力计数值的减小，这正是阿基米德在洗澡盆中悟出的真理：排出的水越多，物体获得的外部支撑力越强。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'ArchimedesPrincipleViz',
  data() {
    return {
      depthVal: 40,
      density: 1.0,
      baseLiquidY: 150
    }
  },
  computed: {
    // Rigid constraint: blockY anchored between [30, 180]
    blockY() { 
       return 50 + this.depthVal * 0.8; 
    },
    displacedVol() {
       // Calculation based on intersection of block (h=60) and base level
       // If bottom of block (blockY + 60) > liquidTop ... 
       const subDepth = Math.max(0, (this.blockY + 60) - this.liquidTop);
       return Math.min(60, subDepth) * 6; // Normalized scale
    },
    liquidTop() {
       // Equilibrium calculation: as block enters, liquid rises
       // Displacement volume / Area. Iterative simplified:
       const rawEntry = Math.max(0, (this.blockY + 60) - this.baseLiquidY);
       const rise = Math.min(60, rawEntry) * 0.2;
       return this.baseLiquidY - rise;
    },
    measuredWeight() {
       // G_total (const) - F_buoyant
       const totalG = 2.5;
       const f_b = (this.displacedVol * this.density) / 100;
       return Math.max(0, totalG - f_b);
    }
  }
}
</script>

<style scoped lang="scss">
.archimedes-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #eff6ff; color: #1e40af; border: 1px solid #bfdbfe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #38bdf8; } &.orange { border-left: 3px solid #f97316; } } }
.interaction-hint { position: absolute; bottom: 10px; width: 100%; text-align: center; color: #334155; font-size: 11px; font-weight: bold; }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .flex-row { flex-direction: row; } .flex-1 { flex: 1; } .gap-6 { gap: 24px; }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #0c4a6e; } } }
</style>
